import dayjs from 'dayjs'
import { ChartData, TimeGrouping } from '@/store/types/DataComparisonType'
import { getColumnDetails } from '@store/slices/DataComparison/types'
import { TimeWindowMode } from '@/features/operations/components/pages/SavedComparison/TimeWindowControl'

export interface FieldConfig {
  valueDataType?: string
  dataSource: {
    id: string
    label: string
  }
  dataDevice?: {
    id: number
    label: string
  }
  dataKey?: {
    id: string
    label: string
  }
  dataTypeSource?: {
    id: string
    label: string
  }
  aggregationType?: {
    id: string
    label: string
  }
  fixedValue?: any
}

export enum ValueDataType {
  NUMBER = 'Number',
  TEXT = 'Text',
}

export enum DataTypeSourceEnum {
  SERIES = 'Series',
  AGGREGATE = 'Aggregate',
}

enum AggregationType {
  LATEST = 'Latest',
  AVERAGE = 'Average',
  SUM = 'Sum',
  MIN = 'Min',
  MAX = 'Max',
}

export enum DataSourceType {
  DEVICE_DATA = 'deviceData',
  FIXED_VALUE = 'fixedValue',
}

export const flattenConfig = (
  config: any,
  parentPath: string = '',
): Array<{ path: string; config: FieldConfig }> => {
  const result: Array<{ path: string; config: FieldConfig }> = []

  Object.entries(config).forEach(([key, value]: [string, any]) => {
    const currentPath = parentPath ? `${parentPath}.${key}` : key

    // Check if this is a field config (has dataSource)
    if (value && typeof value === 'object' && value.dataSource) {
      result.push({
        path: currentPath,
        config: value as FieldConfig,
      })
    }
    // Recurse if it's a nested object without dataSource
    else if (value && typeof value === 'object' && !Array.isArray(value)) {
      result.push(...flattenConfig(value, currentPath))
    }
  })

  return result
}

const getFieldValue = (item: Record<string, any>, dataKey: string): any => {
  if (dataKey in item) return item[dataKey]
  const matchedKey = Object.keys(item).find(
    (key) => key.toLowerCase() === dataKey.toLowerCase(),
  )
  return matchedKey !== undefined ? item[matchedKey] : undefined
}

const extractValue = (
  apiResponse: any,
  dataKey: string,
  dataTypeSource?: string,
  aggregation?: string,
): any => {
  if (dataTypeSource === DataTypeSourceEnum.SERIES) {
    return apiResponse.map((item: any) => {
      let result: any = {
        time: item.time,
        value: getFieldValue(item, dataKey),
      }
      if (item.time) {
        result.timestamp = new Date(item.time).getTime()
      }
      return result
    })
  }

  if (dataTypeSource === DataTypeSourceEnum.AGGREGATE) {
    const valuse = apiResponse.map((it: any) => getFieldValue(it, dataKey))
    let value = valuse.length > 0 ? valuse[valuse.length - 1] : 0
    if (aggregation === AggregationType.AVERAGE) {
      value =
        valuse.reduce((acc: number, curr: number) => acc + curr, 0) /
        valuse.length
    }
    if (aggregation === AggregationType.SUM) {
      value = valuse.reduce((acc: number, curr: number) => acc + curr, 0)
    }
    if (aggregation === AggregationType.MAX) {
      value = Math.max(...valuse)
    }
    if (aggregation === AggregationType.MIN) {
      value = Math.min(...valuse)
    }
    if (aggregation === AggregationType.LATEST) {
      value = valuse[valuse.length - 1]
    }
    if ((value ?? null) === null) value = 0
    return parseFloat(value.toFixed(2))
  }
  return apiResponse
}

const convertValue = (value: any, valueDataType?: string): any => {
  if (!valueDataType) return value
  if (valueDataType === ValueDataType.NUMBER) {
    return Number(value)
  } else if (valueDataType === ValueDataType.TEXT) {
    return String(value)
  }
  return value
}

const setNestedValue = (
  obj: any,
  path: string,
  value: any,
  valueDataType?: string,
): void => {
  const keys = path.split('.')
  const lastKey = keys.pop()!

  let current = obj
  keys.forEach((key) => {
    if (!current[key]) {
      current[key] = {}
    }
    current = current[key]
  })

  current[lastKey] = convertValue(value, valueDataType)
}

const groupByDevice = (
  flattenedConfigs: Array<{ path: string; config: FieldConfig }>,
): Map<number, Array<{ path: string; config: FieldConfig }>> => {
  const grouped = new Map<
    number,
    Array<{ path: string; config: FieldConfig }>
  >()

  flattenedConfigs.forEach((item) => {
    const { config } = item

    // Skip if not device data
    if (config.dataSource.id !== DataSourceType.DEVICE_DATA) {
      return
    }

    if (!config.dataDevice) {
      return
    }

    const deviceId = config.dataDevice.id

    if (!grouped.has(deviceId)) {
      grouped.set(deviceId, [])
    }

    grouped.get(deviceId)!.push(item)
  })

  return grouped
}

export const resolveChartData = async (
  chartConfig: any,
  getDataChart: (params: Record<string, any>) => Promise<any>,
): Promise<any> => {
  const flattenedConfigs = flattenConfig(chartConfig)

  const fixedValues = flattenedConfigs.filter(
    (item) => item.config.dataSource.id === DataSourceType.FIXED_VALUE,
  )

  const deviceDataConfigs = flattenedConfigs.filter(
    (item) => item.config.dataSource.id === DataSourceType.DEVICE_DATA,
  )

  const groupedByDevice = groupByDevice(deviceDataConfigs)

  const apiPromises: Promise<{
    deviceId: number
    data: any
    configs: Array<{ path: string; config: FieldConfig }>
  }>[] = []

  groupedByDevice.forEach((configs, deviceId) => {
    // Get unique data keys for this device
    const dataKeys = Array.from(
      new Set(
        configs
          .filter((item) => item.config.dataKey)
          .map((item) => item.config.dataKey!.id),
      ),
    )

    const promise = getDataChart({
      AssetId: deviceId,
      Names: dataKeys,
    })
      .then((data) => ({
        deviceId,
        data,
        configs,
      }))
      .catch((_) => {
        return {
          deviceId,
          data: null,
          configs,
        }
      })

    apiPromises.push(promise)
  })

  // Step 5: Wait for all API calls
  const apiResults = await Promise.all(apiPromises)

  const result: any = {}

  fixedValues.forEach(({ path, config }) => {
    setNestedValue(result, path, config.fixedValue, config.valueDataType)
  })

  // Add device data
  apiResults.forEach(({ data, configs }) => {
    if (!data) return

    configs.forEach(({ path, config }) => {
      if (!config.dataKey) return
      const value = extractValue(
        data,
        config.dataKey!.id,
        config.dataTypeSource?.id,
        config.aggregationType?.id,
      )
      setNestedValue(result, path, value)
    })
  })
  return result
}

export interface SensorRequest {
  sensorTypeId: string
  sensorId: number
  sensorName: string
  dataColumns: string[]
}

export function parseSensorInfo(sensorInfo: Record<string, boolean> = {}): SensorRequest[] {
  const sensorMap: Record<string, SensorRequest> = {}
  Object.entries(sensorInfo).forEach(([key, enabled]) => {
    if (!enabled) return
    const parts = key.split('+')
    if (parts.length < 4) return
    const [sensorTypeId, sensorId, sensorName, columnName] = parts
    if (!sensorMap[sensorId]) {
      sensorMap[sensorId] = { sensorTypeId, sensorId: Number(sensorId), sensorName, dataColumns: [] }
    }
    if (!sensorMap[sensorId].dataColumns.includes(columnName)) {
      sensorMap[sensorId].dataColumns.push(columnName)
    }
  })
  return Object.values(sensorMap)
}

export function computeDateRange(config: any): { startDate: string; endDate: string } | null {
  if (!config) return null
  const { timeType, timeLast, startDate, endDate } = config
  if (timeType === TimeWindowMode.HISTORY && startDate && endDate) {
    return {
      startDate: dayjs(startDate).startOf('day').utc().format(),
      endDate: dayjs(endDate).endOf('day').utc().format(),
    }
  }
  if (timeType === TimeWindowMode.REALTIME && timeLast?.value) {
    const now = dayjs()
    return {
      startDate: now.subtract(timeLast.value, 'millisecond').utc().format(),
      endDate: now.utc().format(),
    }
  }
  return null
}

export const GROUPING_INTERVAL_MS: Partial<Record<TimeGrouping, number>> = {
  [TimeGrouping.ONE_MIN]: 60 * 1000,
  [TimeGrouping.FIVE_MINS]: 5 * 60 * 1000,
  [TimeGrouping.FIFTEEN_MINS]: 15 * 60 * 1000,
  [TimeGrouping.ONE_HOUR]: 60 * 60 * 1000,
  [TimeGrouping.SIX_HOURS]: 6 * 60 * 60 * 1000,
  [TimeGrouping.TWELVE_HOURS]: 12 * 60 * 60 * 1000,
  [TimeGrouping.ONE_DAY]: 24 * 60 * 60 * 1000,
  [TimeGrouping.TWO_DAYS]: 48 * 60 * 60 * 1000,
}

function groupPoints(
  data: { timestamp: string; value: number }[],
  intervalMs: number,
  isSum: boolean,
  timezoneOffsetMs: number,
): { timestamp: string; value: number }[] {
  const grouped: Record<number, number[]> = {}
  data.forEach((point) => {
    if (point.value === null || point.value === undefined) return
    const t = new Date(point.timestamp).getTime()
    const bucket = Math.floor((t + timezoneOffsetMs) / intervalMs) * intervalMs - timezoneOffsetMs
    if (!grouped[bucket]) grouped[bucket] = []
    grouped[bucket].push(point.value)
  })
  return Object.entries(grouped)
    .map(([key, values]) => ({
      timestamp: new Date(Number(key)).toISOString(),
      value: isSum
        ? values.reduce((s, v) => s + v, 0)
        : values.reduce((s, v) => s + v, 0) / values.length,
    }))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
}

export const fitBucketsToWindow = <T extends { timestamp: string }>(
  points: T[],
  startMs: number,
  endMs: number,
  holdToEnd: boolean,
): T[] => {
  if (!points.length || Number.isNaN(startMs) || Number.isNaN(endMs)) return points
  const timeOf = (point: T) => new Date(point.timestamp).getTime()
  const sorted = [...points].sort((a, b) => timeOf(a) - timeOf(b))
  const lastBeforeStart = sorted.filter((point) => timeOf(point) < startMs).pop()
  const fitted = sorted.filter((point) => timeOf(point) >= startMs)
  if (lastBeforeStart && (!fitted.length || timeOf(fitted[0]) > startMs)) {
    fitted.unshift({ ...lastBeforeStart, timestamp: new Date(startMs).toISOString() })
  }
  const last = fitted[fitted.length - 1]
  if (holdToEnd && last && timeOf(last) < endMs) {
    fitted.push({ ...last, timestamp: new Date(endMs).toISOString() })
  }
  return fitted
}

export function applyTimeGrouping(
  chartData: ChartData[],
  intervalMs: number,
  timezoneOffsetMs: number,
): ChartData[] {
  return chartData.map((series) => ({
    ...series,
    data: groupPoints(series.data as { timestamp: string; value: number }[], intervalMs, series.chartType === 'barChart', timezoneOffsetMs),
  }))
}

export function convertRawToChartData(rawData: any[], sensor: SensorRequest): ChartData[] {
  const { sensorTypeId, sensorId, sensorName } = sensor
  const chartMap: Record<string, ChartData> = {}
  rawData.forEach((item) => {
    const timestamp = item.time as string
    Object.entries(item).forEach(([key, value]) => {
      if (key === 'time') return
      const columnDetail = getColumnDetails(key)
      if (!chartMap[key]) {
        chartMap[key] = {
          sensorId,
          sensorName,
          chartAxisName: columnDetail?.chartAxisName ?? '',
          columnName: columnDetail?.displayName ?? key,
          unitMetric: columnDetail?.metricUnit ?? '',
          unitImperial: columnDetail?.imperialUnit ?? '',
          chartType: columnDetail?.chartType ?? '',
          isFixed: columnDetail?.isFixed ?? 2,
          selectionKey: `${sensorTypeId}+${sensorId}+${sensorName}+${key}`,
          data: [],
        }
      }
      if (value !== null && value !== undefined) {
        chartMap[key].data.push({ timestamp, value: Number(value) })
      }
    })
  })
  return Object.values(chartMap)
}
